import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp } from "@/lib/motion";
import { ArrowRight, Mail, Phone, MapPin, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Συμπλήρωσε όνομα, email και μήνυμα.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Το μήνυμά σου εστάλη. Θα επικοινωνήσω σύντομα μαζί σου.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Κάτι πήγε στραβά. Δοκίμασε ξανά σε λίγο.");
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-20">
        <div className="bg-card rounded-[40px] border border-border/50 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left — copy */}
            <motion.div
              {...fadeUp}
              className="lg:col-span-6 p-10 sm:p-14 lg:p-20 relative"
            >
              <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5">
                Επικοινωνία
              </p>
              <h2
                data-testid="contact-heading"
                className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[64px] leading-[1.04]"
              >
                Κλείσε <span className="italic font-light">την πρώτη</span>
                <br />
                σου συνεδρία.
              </h2>
              <p className="mt-7 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-[480px]">
                Το πρώτο βήμα είναι συχνά το πιο δύσκολο. Είμαι εδώ για να το
                κάνουμε μαζί — με τον δικό σου ρυθμό, χωρίς πίεση.
              </p>

              <div className="mt-10 space-y-5 font-body">
                <a
                  href="mailto:anniepapach@gmail.com"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 text-foreground/80 hover:text-foreground group transition-colors"
                >
                  <span className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border/50 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Mail size={16} strokeWidth={1.5} />
                  </span>
                  <span className="text-base">anniepapach@gmail.com</span>
                </a>
                <a
                  href="tel:+306978042360"
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 text-foreground/80 hover:text-foreground group transition-colors"
                >
                  <span className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border/50 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Phone size={16} strokeWidth={1.5} />
                  </span>
                  <span className="text-base">697 8042 360</span>
                </a>
                <div className="flex items-start gap-4 text-foreground/80">
                  <span className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border/50 shrink-0">
                    <MapPin size={16} strokeWidth={1.5} />
                  </span>
                  <span className="text-base leading-relaxed">
                    Ρήγα Φεραίου 119
                    <br />
                    <span className="text-muted-foreground">Πάτρα, Ελλάδα</span>
                  </span>
                </div>
              </div>

              <Link
                to="/rantevou"
                data-testid="contact-booking-link"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-primary/10 border border-primary/30 text-foreground px-6 py-4 font-body text-sm font-medium hover:bg-primary/20 transition-all duration-300 group"
              >
                <CalendarCheck size={16} strokeWidth={1.5} className="text-primary" />
                Κλείσε ραντεβού online — δες διαθέσιμες ώρες
                <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            {/* Right — form */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="lg:col-span-6 bg-background p-10 sm:p-14 lg:p-16 border-t lg:border-t-0 lg:border-l border-border/50"
            >
              <form onSubmit={onSubmit} className="space-y-6">
                <Field
                  label="Ονοματεπώνυμο"
                  id="name"
                  value={form.name}
                  onChange={update("name")}
                  testId="contact-input-name"
                />
                <Field
                  label="Email"
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  testId="contact-input-email"
                />
                <Field
                  label="Τηλέφωνο (προαιρετικό)"
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  testId="contact-input-phone"
                />
                <div>
                  <label
                    htmlFor="message"
                    className="block text-[11px] tracking-[0.22em] uppercase font-body font-medium text-muted-foreground mb-2"
                  >
                    Σύντομο μήνυμα
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    className="w-full bg-transparent border-0 border-b border-border focus:border-foreground outline-none py-3 font-body text-base text-foreground placeholder:text-muted-foreground/60 transition-colors resize-none"
                    placeholder="Πες μου λίγα λόγια για αυτό που σε φέρνει εδώ…"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-background px-8 py-4 font-body text-base font-medium transition-all duration-300 hover:-translate-y-0.5 hover:bg-foreground/85 disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                >
                  {loading ? "Αποστολή…" : "Επικοινώνησε μαζί μου"}
                  <ArrowRight
                    size={18}
                    strokeWidth={1.5}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>

                <p className="text-xs font-body text-muted-foreground leading-relaxed pt-2">
                  Με την αποστολή του μηνύματος αποδέχεσαι την επεξεργασία των
                  στοιχείων σου αποκλειστικά για επικοινωνία σχετικά με συνεδρία.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[11px] tracking-[0.22em] uppercase font-body font-medium text-muted-foreground mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-transparent border-0 border-b border-border focus:border-foreground outline-none py-3 font-body text-base text-foreground placeholder:text-muted-foreground/60 transition-colors"
      />
    </div>
  );
}
